"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import styles from "./RangeSlider.module.scss";
import {
  CONVERSION_FACTORS,
  UNIT_LABELS,
} from "@constants/priceRangeConstants";
import { formatInputValue, parseInputValue } from "@helpers/rangeSliderHelper";

// Unit thresholds for determining display units
const UNIT_THRESHOLDS = {
  CRORE: CONVERSION_FACTORS.CRORE,
  LAKH: CONVERSION_FACTORS.LAKH,
} as const;

interface SingleInputDualSliderProps {
  min?: number;
  max?: number;
  step?: number;
  minValue?: number;
  maxValue?: number;
  onChange?: (min: number, max: number) => void;
}

// Utility function to determine unit based on value
const getUnitForValue = (value: number): string => {
  if (value >= UNIT_THRESHOLDS.CRORE) {
    return UNIT_LABELS.CRORE;
  } else if (value >= UNIT_THRESHOLDS.LAKH) {
    return UNIT_LABELS.LAKH;
  } else {
    return UNIT_LABELS.THOUSAND;
  }
};

const RangeSlider: React.FC<SingleInputDualSliderProps> = ({
  min = 0,
  max = 100,
  step = 1,
  minValue = min,
  maxValue = max,
  onChange,
}) => {
  const [minVal, setMinVal] = useState(minValue);
  const [maxVal, setMaxVal] = useState(maxValue);
  const [activeHandle, setActiveHandle] = useState<"min" | "max" | null>(null);
  const [minInputValue, setMinInputValue] = useState(
    formatInputValue(minValue)
  );
  const [maxInputValue, setMaxInputValue] = useState(
    formatInputValue(maxValue)
  );
  const [isMinInputFocused, setIsMinInputFocused] = useState(false);
  const [isMaxInputFocused, setIsMaxInputFocused] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Update input values when slider values change
  useEffect(() => {
    if (!isMinInputFocused) {
      setMinInputValue(formatInputValue(minVal));
    }
    if (!isMaxInputFocused) {
      setMaxInputValue(formatInputValue(maxVal));
    }
  }, [minVal, maxVal, isMinInputFocused, isMaxInputFocused]);

  // Update slider values when props change
  useEffect(() => {
    setMinVal(minValue);
    setMaxVal(maxValue);
  }, [minValue, maxValue]);

  const getPercent = (value: number) => ((value - min) / (max - min)) * 100;

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinInputValue(value);
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxInputValue(value);
  };

  const handleMinInputBlur = () => {
    setIsMinInputFocused(false);
    const unit = getUnitForValue(minVal);
    const newMin = parseInputValue(minInputValue, unit);
    const validatedMin = Math.max(min, Math.min(newMin, maxVal - step));
    setMinVal(validatedMin);
    setMinInputValue(formatInputValue(validatedMin));
    onChange?.(validatedMin, maxVal);
  };

  const handleMaxInputBlur = () => {
    setIsMaxInputFocused(false);
    const unit = getUnitForValue(maxVal);
    const newMax = parseInputValue(maxInputValue, unit);
    const validatedMax = Math.min(max, Math.max(newMax, minVal + step));
    setMaxVal(validatedMax);
    setMaxInputValue(formatInputValue(validatedMax));
    onChange?.(minVal, validatedMax);
  };

  const handleMinInputFocus = () => {
    setIsMinInputFocused(true);
  };

  const handleMaxInputFocus = () => {
    setIsMaxInputFocused(true);
  };

  const handleSliderClick = (event: React.MouseEvent) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = (clickX / rect.width) * 100;
    const value = Math.round((percentage / 100) * (max - min) + min);

    const distanceToMin = Math.abs(value - minVal);
    const distanceToMax = Math.abs(value - maxVal);

    if (distanceToMin < distanceToMax) {
      const newMin = Math.max(min, Math.min(value, maxVal - step));
      setMinVal(newMin);
      onChange?.(newMin, maxVal);
    } else {
      const newMax = Math.min(max, Math.max(value, minVal + step));
      setMaxVal(newMax);
      onChange?.(minVal, newMax);
    }
  };

  const handleMouseDown = (event: React.MouseEvent, handle: "min" | "max") => {
    event.preventDefault();
    setActiveHandle(handle);

    const handleMouseMove = (e: MouseEvent) => {
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const moveX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (moveX / rect.width) * 100));
      const value = Math.round((percentage / 100) * (max - min) + min);

      if (handle === "min") {
        const newMin = Math.max(min, Math.min(value, maxVal - step));
        setMinVal(newMin);
        onChange?.(newMin, maxVal);
      } else {
        const newMax = Math.min(max, Math.max(value, minVal + step));
        setMaxVal(newMax);
        onChange?.(minVal, newMax);
      }
    };

    const handleMouseUp = () => {
      setActiveHandle(null);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const minPercent = getPercent(minVal);
  const maxPercent = getPercent(maxVal);
  const formatPrice = useCallback((value: number) => {
    if (value >= CONVERSION_FACTORS.CRORE) {
      return {
        value: `${(value / CONVERSION_FACTORS.CRORE).toFixed(1)}`,
        unit: "Cr",
      };
    } else if (value >= CONVERSION_FACTORS.LAKH) {
      return {
        value: `${(value / CONVERSION_FACTORS.LAKH).toFixed(1)}`,
        unit: "L",
      };
    } else {
      return {
        value: `${(value / CONVERSION_FACTORS.THOUSAND).toFixed(0)}`,
        unit: "K",
      };
    }
  }, []);

  return (
    <div>
      <div
        ref={sliderRef}
        className={`${styles.slider}`}
        onClick={handleSliderClick}
      >
        <div className={styles.track} />
        <div
          className={styles.range}
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />
        <div
          className={`${styles.handle} ${styles.handleMin} ${
            activeHandle === "min" ? styles.active : ""
          }`}
          style={{ left: `${minPercent}%` }}
          onMouseDown={(e) => handleMouseDown(e, "min")}
        />
        <div
          className={`${styles.handle} ${styles.handleMax} ${
            activeHandle === "max" ? styles.active : ""
          }`}
          style={{ left: `${maxPercent}%` }}
          onMouseDown={(e) => handleMouseDown(e, "max")}
        />
      </div>

      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <span className={styles.currencySymbol}>₹</span>
          <input
            name="min"
            type="text"
            className={styles.inputField}
            value={minInputValue}
            onChange={handleMinInputChange}
            onFocus={handleMinInputFocus}
            onBlur={handleMinInputBlur}
            placeholder="0"
          />
          <span className={styles.inputLabel}>{formatPrice(minVal).unit}</span>
        </div>

        <div className={styles.inputWrapper}>
          <span className={styles.currencySymbol}>₹</span>
          <input
            type="text"
            name="max"
            className={styles.inputField}
            value={maxInputValue}
            onChange={handleMaxInputChange}
            onFocus={handleMaxInputFocus}
            onBlur={handleMaxInputBlur}
            placeholder="0"
          />
          <span className={styles.inputLabel}>{formatPrice(maxVal).unit}</span>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
